import type { routes, SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import { getEmitterDataForRows } from 'App/Map/PanelDetails/StackemCharts/_utils/by-domain/soil/_utils/getEmitterDataForRows'
import { getHasWaterEvents } from 'App/Map/PanelDetails/StackemCharts/_utils/by-domain/soil/_utils/getHasWaterEvents'
import { makeAwcChart } from 'App/Map/PanelDetails/StackemCharts/_utils/by-domain/soil/_utils/makeAwcChart'
import { makeEmitterSeries } from 'App/Map/PanelDetails/StackemCharts/_utils/by-domain/soil/_utils/makeEmitterSeries'
import { makeRainSeries } from 'App/Map/PanelDetails/StackemCharts/_utils/by-domain/soil/_utils/makeRainSeries'
import { makeWaterEventsChart } from 'App/Map/PanelDetails/StackemCharts/_utils/by-domain/soil/_utils/makeWaterEventsChart'
import { PREDICTION_KEY } from 'App/Map/PanelDetails/StackemCharts/_utils/by-domain/soil/_utils/predictionKey'
import type { TLngLat } from 'App/Map/types'
import type {
  StackedChartSection,
  StackedChartSectionItem,
  TIrrigationEventSeries,
} from 'components/StackedChart/types'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'

export const buildAwcLineAndWaterEvents = ({
  data,
  selectedProperty,
  soilStationLngLat,
  precipitationStationLngLat,
  compareSeasonsData,
  soilStationFavoriteDepths,
}: {
  data: routes.Values.Response
  selectedProperty: TProperty
  precipitationStationLngLat: string | null
  soilStationLngLat: string | null
  compareSeasonsData: routes.Values.Response
  soilStationFavoriteDepths: SharedTypes.TSoilSensorDepth[]
}): StackedChartSection => {
  const commonReturnItems = {
    title: '',
    id: 'ics-soil-awc-line',
  }

  const soilMoisture = data?.points?.[String(soilStationLngLat)]?.values?.soilMoisture || []

  const firstTimestampFromPredictionSeries =
    soilMoisture.find((depth) => depth.metadata.depth === PREDICTION_KEY)?.timeseries[0]?.timestamp || 0

  const firstForecastTimestamp = firstTimestampFromPredictionSeries
    ? +new Date(firstTimestampFromPredictionSeries)
    : +new Date()

  const aggregationInterval = soilMoisture[0]?.metadata?.aggregationInterval
  const { compareSeasonsInterval } = detailsPanelStore.getState()
  const { timezone } = selectedProperty

  const rainData =
    data?.points?.[String(precipitationStationLngLat)]?.values?.precipitation?.[0]?.timeseries ?? []

  const emitterData = soilMoisture?.[0]?.metadata?.irrigationActivityForZones ?? {}

  const zoneEmitterIds =
    (soilStationLngLat &&
      selectedProperty.points?.[soilStationLngLat as TLngLat]?.configuration
        ?.soilProbeIntersectingZoneIdsWithEmitters) ||
    []

  const rainSeries: TIrrigationEventSeries = makeRainSeries(rainData)
  const hasRain = rainSeries.data.length > 0

  const emitterRows = getEmitterDataForRows({
    availableEmitters: zoneEmitterIds,
    emitterData,
    irrigationActivityScope: 'ZONE',
  })

  const emitterSeries = makeEmitterSeries(emitterRows, hasRain, true)
  const hasWaterEvents = getHasWaterEvents({ hasRain, emitterData: emitterRows })

  // the legend lists every row, so a scheduled event for a device with no PT has a legend entry too
  const availableEmittersForStation = emitterSeries.map(
    ({ irrigationZoneEmitterId }) => irrigationZoneEmitterId,
  )

  const chartItems: StackedChartSectionItem[] = []

  if (hasWaterEvents) {
    chartItems.push(
      makeWaterEventsChart({
        waterEventsSeries: hasRain ? [rainSeries, ...emitterSeries] : emitterSeries,
        rainSeries,
        firstForecastTimestamp: +new Date(),
        timezone,
        isDaily: aggregationInterval === 'DAILY',
      }),
    )
  }

  chartItems.push(
    makeAwcChart({
      aggregationInterval,
      availableEmittersForStation,
      hasRain,
      hasWaterEvents,
      chartId: commonReturnItems.id,
      compareSeasonsData,
      compareSeasonsInterval,
      data,
      firstForecastTimestamp,
      soilStationLngLat,
      soilStationFavoriteDepths,
      showSelectedSoilDepthsOverride: false,
    }),
  )

  return {
    ...commonReturnItems,
    items: chartItems,
  }
}
